import { FormEvent, useEffect, useId, useState } from "react";
import { useSearchParams } from "react-router-dom";

import { api } from "../../api/client";
import { AlertBanner } from "../../components/feedback/AlertBanner";
import { Field } from '../../components/forms/Field';
import {
  BirthsSubnav,
  type ReproductionArchiveKind,
} from '../../components/subnav/BirthsSubnav';
import { apiError } from "../../lib/apiError";
import { formatDate } from "../../lib/format";
import { passwordFieldProps } from "../../lib/inputPrivacy";
import { useModalHistoryEntry } from "../../lib/modalHistory";
import type { BreedingSeason, BreedingSeasonDetail, BirthSeason, BirthSeasonDetail } from "../../types";

function parseArchiveKind(value: string | null): ReproductionArchiveKind {
  return value === "breeding" ? "breeding" : "births";
}

export function BirthsArchivePage() {
  const fieldNs = useId().replace(/:/g, "");
  const [searchParams, setSearchParams] = useSearchParams();
  const kind = parseArchiveKind(searchParams.get("kind"));

  const [lambingSeasons, setBirthSeasons] = useState<BirthSeason[]>([]);
  const [breedingSeasons, setBreedingSeasons] = useState<BreedingSeason[]>([]);
  const [selectedId, setSelectedId] = useState<number | null>(null);
  const [lambingDetail, setLambingDetail] = useState<BirthSeasonDetail | null>(null);
  const [breedingDetail, setBreedingDetail] = useState<BreedingSeasonDetail | null>(null);
  const [error, setError] = useState<string | null>(null);
  const [busy, setBusy] = useState(false);
  const [deleteOpen, setDeleteOpen] = useState(false);
  const [password, setPassword] = useState("");

  useModalHistoryEntry(deleteOpen, () => {
    setDeleteOpen(false);
    setPassword("");
  });

  function setKind(next: ReproductionArchiveKind) {
    setSearchParams(next === "births" ? { kind: "births" } : { kind: "breeding" });
    setSelectedId(null);
    setLambingDetail(null);
    setBreedingDetail(null);
    setError(null);
  }

  async function load() {
    if (kind === "breeding") {
      const result = await api.GET("/api/breeding-seasons", {
        params: { query: { scope: "archived" } },
      });
      if (result.error) {
        setError(apiError(result.error, "Nie udało się wczytać archiwum stanówek."));
        return;
      }
      setError(null);
      setBreedingSeasons((result.data as BreedingSeason[]) ?? []);
      return;
    }

    const result = await api.GET("/api/birth-seasons", {
      params: { query: { scope: "archived" } },
    });
    if (result.error) {
      setError(apiError(result.error, "Nie udało się wczytać archiwum wykotów."));
      return;
    }
    setError(null);
    setBirthSeasons((result.data as BirthSeason[]) ?? []);
  }

  async function loadLambingDetail(seasonId: number) {
    setBusy(true);
    const result = await api.GET("/api/birth-seasons/{season_id}", {
      params: { path: { season_id: seasonId } },
    });
    setBusy(false);
    if (result.error) {
      setError(apiError(result.error, "Nie udało się wczytać szczegółów sezonu."));
      setLambingDetail(null);
      return;
    }
    setError(null);
    setLambingDetail(result.data as BirthSeasonDetail);
  }

  async function loadBreedingDetail(seasonId: number) {
    setBusy(true);
    const result = await api.GET("/api/breeding-seasons/{season_id}", {
      params: { path: { season_id: seasonId } },
    });
    setBusy(false);
    if (result.error) {
      setError(apiError(result.error, "Nie udało się wczytać szczegółów stanówki."));
      setBreedingDetail(null);
      return;
    }
    setError(null);
    setBreedingDetail(result.data as BreedingSeasonDetail);
  }

  useEffect(() => {
    setSelectedId(null);
    setLambingDetail(null);
    setBreedingDetail(null);
    void load();
  }, [kind]);

  useEffect(() => {
    if (selectedId == null) {
      setLambingDetail(null);
      setBreedingDetail(null);
      return;
    }
    if (kind === "breeding") {
      void loadBreedingDetail(selectedId);
    } else {
      void loadLambingDetail(selectedId);
    }
  }, [selectedId, kind]);

  async function restore(seasonId: number) {
    setBusy(true);
    const result =
      kind === "breeding"
        ? await api.POST("/api/breeding-seasons/{season_id}/restore", {
            params: { path: { season_id: seasonId } },
            body: {},
          })
        : await api.POST("/api/birth-seasons/{season_id}/restore", {
            params: { path: { season_id: seasonId } },
            body: {},
          });
    setBusy(false);
    if (result.error) {
      setError(apiError(result.error, "Nie udało się przywrócić grupy."));
      return;
    }
    if (selectedId === seasonId) {
      setSelectedId(null);
      setLambingDetail(null);
      setBreedingDetail(null);
    }
    await load();
  }

  async function onDelete(event: FormEvent) {
    event.preventDefault();
    if (selectedId == null || !password) {
      setError("Podaj hasło administratora.");
      return;
    }
    setBusy(true);
    const deleted =
      kind === "breeding"
        ? await api.DELETE("/api/breeding-seasons/{season_id}", {
            params: { path: { season_id: selectedId } },
            body: { password },
          })
        : await api.DELETE("/api/birth-seasons/{season_id}", {
            params: { path: { season_id: selectedId } },
            body: { password },
          });
    setBusy(false);
    if (deleted.error) {
      setError(apiError(deleted.error, "Nie udało się usunąć grupy."));
      return;
    }
    setDeleteOpen(false);
    setPassword("");
    setSelectedId(null);
    setLambingDetail(null);
    setBreedingDetail(null);
    await load();
  }

  const seasons = kind === "breeding" ? breedingSeasons : lambingSeasons;
  const detail = kind === "breeding" ? breedingDetail : lambingDetail;

  return (
    <main className="page" data-testid="lambing-archive-page">
      <header className="page__header">
        <p className="page__kicker">Rozród</p>
        <h1>Archiwum</h1>
      </header>
      <BirthsSubnav area="archive" archiveKind={kind} />
      <AlertBanner variant="error" message={error} onDismiss={() => setError(null)} />

      <nav className="tabs" aria-label="Rodzaj archiwum">
        <button
          type="button"
          className={kind === "breeding" ? "tab tab--active" : "tab"}
          data-testid="archive-kind-breeding"
          onClick={() => setKind("breeding")}
        >
          Stanówki
        </button>
        <button
          type="button"
          className={kind === "births" ? "tab tab--active" : "tab"}
          data-testid="archive-kind-lambings"
          onClick={() => setKind("births")}
        >
          Wykoty
        </button>
      </nav>

      <section className="panel">
        {seasons.length === 0 ? (
          <p className="muted">
            {kind === "breeding"
              ? "Brak zarchiwizowanych grup stanówki."
              : "Brak zarchiwizowanych grup wykotów."}
          </p>
        ) : (
          <ul className="list" data-testid="lambing-archive-list">
            {kind === "breeding"
              ? breedingSeasons.map((season) => (
                  <li key={season.id} className="list__item">
                    <div>
                      <button
                        type="button"
                        className="btn--link"
                        data-testid="lambing-archive-select"
                        onClick={() =>
                          setSelectedId((current) => (current === season.id ? null : season.id))
                        }
                      >
                        <strong>{season.name}</strong>
                      </button>
                      <p className="muted">
                        owce {season.dam_count} · tryki {season.sire_count} · pokryte{" "}
                        {season.mated_count}
                        {season.breed ? ` · ${season.breed}` : ""}
                        {season.start_date ? ` · start ${formatDate(season.start_date)}` : ""}
                      </p>
                    </div>
                    <div className="actions">
                      <button
                        type="button"
                        className="btn btn--ghost btn--sm"
                        disabled={busy}
                        data-testid="lambing-archive-restore"
                        onClick={() => void restore(season.id)}
                      >
                        Przywróć
                      </button>
                    </div>
                  </li>
                ))
              : lambingSeasons.map((season) => (
                  <li key={season.id} className="list__item">
                    <div>
                      <button
                        type="button"
                        className="btn--link"
                        data-testid="lambing-archive-select"
                        onClick={() =>
                          setSelectedId((current) => (current === season.id ? null : season.id))
                        }
                      >
                        <strong>{season.name}</strong>
                      </button>
                      <p className="muted">
                        {season.event_count} wykotów · {season.offspring_count} jagniąt
                        {season.breed ? ` · ${season.breed}` : ""}
                      </p>
                    </div>
                    <div className="actions">
                      <button
                        type="button"
                        className="btn btn--ghost btn--sm"
                        disabled={busy}
                        data-testid="lambing-archive-restore"
                        onClick={() => void restore(season.id)}
                      >
                        Przywróć
                      </button>
                    </div>
                  </li>
                ))}
          </ul>
        )}
      </section>

      {selectedId != null ? (
        <section className="panel" data-testid="lambing-archive-detail">
          {busy && !detail ? <p className="muted">Wczytywanie szczegółów…</p> : null}
          {kind === "breeding" && breedingDetail ? (
            <>
              <div className="panel__header">
                <div>
                  <h2 className="panel__title">{breedingDetail.name}</h2>
                  <p className="muted">
                    owce {breedingDetail.dam_count} · tryki {breedingDetail.sire_count} · pokryte{" "}
                    {breedingDetail.mated_count}
                    {breedingDetail.breed ? ` · rasa: ${breedingDetail.breed}` : " · bez rasy"}
                  </p>
                </div>
                <div className="panel__header-actions actions">
                  <button
                    type="button"
                    className="btn btn--ghost btn--sm"
                    disabled={busy}
                    data-testid="lambing-archive-restore-detail"
                    onClick={() => void restore(breedingDetail.id)}
                  >
                    Przywróć
                  </button>
                  <button
                    type="button"
                    className="btn btn--danger btn--sm"
                    data-testid="lambing-archive-delete"
                    disabled={busy}
                    onClick={() => {
                      setPassword("");
                      setDeleteOpen(true);
                    }}
                  >
                    Usuń trwale
                  </button>
                </div>
              </div>
              <div className="kpi" data-testid="lambing-archive-detail-stats">
                <article className="kpi__card kpi__card--plain">
                  <p className="kpi__label">Nazwa</p>
                  <p className="kpi__value">{breedingDetail.name}</p>
                </article>
                <article className="kpi__card kpi__card--plain">
                  <p className="kpi__label">Rasa</p>
                  <p className="kpi__value">{breedingDetail.breed || "—"}</p>
                </article>
                <article className="kpi__card kpi__card--plain">
                  <p className="kpi__label">Owce</p>
                  <p className="kpi__value">{breedingDetail.dam_count}</p>
                </article>
                <article className="kpi__card kpi__card--plain">
                  <p className="kpi__label">Tryki</p>
                  <p className="kpi__value">{breedingDetail.sire_count}</p>
                </article>
              </div>
            </>
          ) : null}
          {kind === "births" && lambingDetail ? (
            <>
              <div className="panel__header">
                <div>
                  <h2 className="panel__title">{lambingDetail.name}</h2>
                  <p className="muted">
                    {lambingDetail.event_count} wykotów · {lambingDetail.offspring_count} jagniąt
                    {lambingDetail.breed ? ` · rasa: ${lambingDetail.breed}` : " · bez rasy"}
                  </p>
                </div>
                <div className="panel__header-actions actions">
                  <button
                    type="button"
                    className="btn btn--ghost btn--sm"
                    disabled={busy}
                    data-testid="lambing-archive-restore-detail"
                    onClick={() => void restore(lambingDetail.id)}
                  >
                    Przywróć
                  </button>
                  <button
                    type="button"
                    className="btn btn--danger btn--sm"
                    data-testid="lambing-archive-delete"
                    disabled={busy}
                    onClick={() => {
                      setPassword("");
                      setDeleteOpen(true);
                    }}
                  >
                    Usuń trwale
                  </button>
                </div>
              </div>
              <div className="kpi" data-testid="lambing-archive-detail-stats">
                <article className="kpi__card kpi__card--plain">
                  <p className="kpi__label">Nazwa</p>
                  <p className="kpi__value">{lambingDetail.name}</p>
                </article>
                <article className="kpi__card kpi__card--plain">
                  <p className="kpi__label">Rasa</p>
                  <p className="kpi__value">{lambingDetail.breed || "—"}</p>
                </article>
                <article className="kpi__card kpi__card--plain">
                  <p className="kpi__label">Wykoty</p>
                  <p className="kpi__value">{lambingDetail.event_count}</p>
                </article>
                <article className="kpi__card kpi__card--plain">
                  <p className="kpi__label">Jagnięta</p>
                  <p className="kpi__value">{lambingDetail.offspring_count}</p>
                </article>
              </div>
            </>
          ) : null}
        </section>
      ) : null}

      {deleteOpen ? (
        <div
          className="modal-overlay"
          role="dialog"
          aria-modal="true"
          data-testid="lambing-archive-delete-modal"
        >
          <form className="modal" onSubmit={onDelete}>
            <h3 className="modal__title">Usuń grupę trwale</h3>
            <p className="modal__text">
              {kind === "breeding" || lambingDetail?.breeding_season_id != null ? (
                <>
                  <strong>Tej operacji nie da się cofnąć.</strong> Usunięcie usunie też powiązaną
                  grupę {kind === "breeding" ? "wykotów" : "stanówki"} — nie będzie możliwości jej
                  przywrócenia.
                </>
              ) : (
                <>
                  <strong>Tej operacji nie da się cofnąć.</strong> Zostaną usunięte wszystkie wykoty
                  i jagnięta w tej grupie.
                </>
              )}
            </p>
            <p className="modal__text muted">
              Operacja zawsze wymaga hasła administratora — także wtedy, gdy w ustawieniach
              wyłączono potwierdzanie hasłem przy edycji.
            </p>
            <Field label="Hasło administratora (wymagane)">
              <input
                className="input"
                type="password"
                data-testid="lambing-archive-delete-password"
                value={password}
                onChange={(event) => setPassword(event.target.value)}
                required
                {...passwordFieldProps(`${fieldNs}-archive-delete-password`)}
              />
            </Field>
            <div className="modal__actions">
              <button
                type="button"
                className="btn btn--ghost"
                onClick={() => {
                  setDeleteOpen(false);
                  setPassword("");
                }}
              >
                Anuluj
              </button>
              <button
                type="submit"
                className="btn btn--danger"
                data-testid="lambing-archive-delete-confirm"
                disabled={busy}
              >
                Usuń trwale
              </button>
            </div>
          </form>
        </div>
      ) : null}
    </main>
  );
}
